import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { HyperframesPlayer, type HfPlayerRef } from "../../hyperframesPlayer";
import {
  FORMAT_DIMENSIONS,
  FPS,
  calcReelDurationFrames,
  buildReelComposition,
} from "../../lib/hyperframesCompositions";
import type {
  OverlayLayer,
  ReelScene,
  WatermarkConfig,
  MusicConfig,
  VoiceoverConfig,
  SfxEvent,
  SfxConfig,
} from "../../api";
import { PlayerEditOverlay, type SceneOffsetPatch } from "./PlayerEditOverlay";
import type { QueueItem } from "@engine/lib/queue";
import type { FormatId } from "@engine/hyperframes/format";

// Backwards-compatible alias — call sites still use PlayerRef as a generic
// handle for the preview player. We re-name to HfPlayerRef going forward.
type PlayerRef = HfPlayerRef;

/**
 * React error boundary that catches errors thrown by the <Player> itself or
 * by anything inside its render path that the per-scene boundaries didn't
 * catch (e.g. brand provider, format hook, audio loader). Without this, an
 * uncaught error blanks the whole preview area with no recovery affordance.
 *
 * The fallback exposes a "↻ Player neu starten" button that bumps the
 * remountKey via the same callback the Composer's Reset button uses.
 */
class PlayerErrorBoundary extends React.Component<
  { onReset: () => void; children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { onReset: () => void; children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[Player] crashed:", error, info.componentStack);
  }
  handleReset = () => {
    this.setState({ error: null });
    this.props.onReset();
  };
  render() {
    if (this.state.error) {
      return (
        <div className="player-shell player-error-shell">
          <div className="player-error-icon">⚠️</div>
          <div className="player-error-title">Player-Fehler</div>
          <div className="player-error-msg">{String(this.state.error.message || this.state.error)}</div>
          <button className="btn primary player-error-reset" onClick={this.handleReset}>
            ↻ Player neu starten
          </button>
          <div className="player-error-hint">
            Tipp: Wenn das hier öfter passiert, prüfe die Konsole (⌘⌥I) — meist ist es eine
            einzelne Scene mit ungültigen Inputs.
          </div>
        </div>
      );
    }
    return this.props.children as React.ReactElement;
  }
}

type Props = {
  scenes: ReelScene[];
  /** Free-form text/sticker overlays that render ABOVE the scene composition. */
  overlays?: OverlayLayer[];
  format: string;
  maxWidth?: number;
  safeZone?: { enabled: boolean; platform: "tiktok" | "reels" | "shorts" | "stories" | "general" };
  /** Render-Theme der Vorschau ("dark" | "light"). Default = CI-Theme. */
  theme?: "dark" | "light";
  watermark?: WatermarkConfig;
  /**
   * Effective watermark of the currently-selected scene. Used SOLELY by
   * the in-canvas drag handle so the user grabs the correct per-scene
   * logo (each clip now renders its own logo via the engine's per-clip
   * watermark pipeline). When undefined, no logo handle is shown — the
   * user must select a scene first.
   */
  editWatermark?: WatermarkConfig;
  /** UID of the scene whose logo the edit overlay should anchor on. */
  editWatermarkSceneUid?: string;
  music?: MusicConfig;
  /** Voiceover-Spur (eine pro Reel). */
  voiceover?: VoiceoverConfig;
  /** Unabhängige SFX-Events auf der Timeline. */
  sfxEvents?: SfxEvent[];
  /**
   * Reel-weite SFX-Master (Volume + Mute) — wird durch die Engine an
   * <Reel> als inputProp weitergereicht. Wichtig: dies ersetzt das alte
   * Verhalten, beim Mute `sfxEvents=[]` zu schicken — dort wurden die
   * Audio-Tags aus dem Tree entfernt, was die Music-Spur stören konnte.
   */
  sfxConfig?: SfxConfig;
  /**
   * Fires EXACTLY ONCE per Player instance — not on every render.
   * `null` means the previous instance was just unmounted (clean up listeners).
   */
  onPlayerReady?: (player: PlayerRef | null) => void;

  /**
   * Currently selected layer uid — can be either a scene uid or an overlay
   * uid. The PlayerEditOverlay decides which kind it is by looking it up
   * in `scenes` / `overlays`.
   */
  selectedUid?: string | null;
  /** Set to "" (empty string) for explicit deselect. */
  onSelectUid?: (uid: string) => void;
  /** Current playhead frame — overlays only show edit handles when visible at this frame. */
  playheadFrame?: number;
  onWatermarkChange?: (next: WatermarkConfig) => void;
  onScenePatch?: (uid: string, patch: SceneOffsetPatch) => void;
  /** Patch any overlay (position / size / text / etc). The composer dispatches
   *  the patch to the kind-aware updater. */
  onOverlayPatch?: (uid: string, patch: Partial<OverlayLayer>) => void;
  /** Quick-add a new text overlay at the playhead — used by the "+ T" button in the overlay. */
  onAddTextOverlay?: () => void;
  /** Receive a drop from the element library. xPct/yPct are in composition %. */
  onDropElement?: (elementId: string, xPct: number, yPct: number) => void;

  /** Player playback options (driven by the global TransportBar). */
  loop?: boolean;
  /** Initial mute state (the toggling itself happens on the PlayerRef). */
  initialMuted?: boolean;
  /**
   * Selektives SFX-Mute (Master-Schalter im Studio). Wird live an den
   * Player durchgereicht und mutet ausschließlich Audio-Elemente mit
   * `data-audio-kind="sfx"`. Die Musik-Spur bleibt davon unberührt — und
   * die Composition wird beim Toggle NICHT neu gebaut (kein iframe-Reload,
   * kein Bildstottern, kein Music-Aussetzer).
   */
  sfxMuted?: boolean;
  /** Mute pro Audio-Kind — alle Toggles wirken zur Laufzeit, OHNE die
   *  Composition neu zu bauen oder das iframe zu reloaden. */
  musicMuted?: boolean;
  voiceoverMuted?: boolean;
  /** Disable direct-manipulation editing in the preview image AND built-in player chrome. */
  locked?: boolean;
  /** Snap dragged elements to a grid; renders grid overlay during drag. */
  snap?: boolean;
  /** Bump to force a clean Player remount — recovers from the "black screen" stall. */
  remountKey?: number;
  /** Forwarded to the overlay → Composer pauses the player while the user drags. */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /** Called by the Player error boundary's "Reset" button — Composer should bump remountKey. */
  onPlayerReset?: () => void;
};

export type ReelPlayerHandle = {
  player: PlayerRef | null;
  durationInFrames: number;
};

/**
 * Signature used as React key for <Player>. We INTENTIONALLY only include the
 * format and the *count + order* of scenes — not durations, inputs, transitions,
 * or fromSec/track. Those flow through inputProps live and don't need a remount.
 *
 * Reason: every keystroke in the inspector or every drag tick would otherwise
 * remount the Player. A remount triggers a fresh play() call which the browser's
 * autoplay policy blocks → "black screen, play does nothing".
 */
function reelStructureKey(scenes: ReelScene[], format: string): string {
  return [format, scenes.length, ...scenes.map((s) => `${s.uid}:${s.sceneId}`)].join("|");
}

export const ReelPlayerPreview = forwardRef<ReelPlayerHandle, Props>(
  (
    {
      scenes,
      overlays,
      format,
      maxWidth,
      safeZone,
      theme,
      watermark,
      editWatermark,
      editWatermarkSceneUid,
      music,
      voiceover,
      sfxEvents,
      sfxConfig,
      onPlayerReady,
      selectedUid = null,
      onSelectUid,
      playheadFrame = 0,
      onWatermarkChange,
      onScenePatch,
      onOverlayPatch,
      onAddTextOverlay,
      onDropElement,
      loop = true,
      initialMuted = false,
      sfxMuted = false,
      musicMuted = false,
      voiceoverMuted = false,
      locked = false,
      snap = false,
      remountKey = 0,
      onDragStart,
      onDragEnd,
      onPlayerReset,
    },
    ref,
  ) => {
    const dims = FORMAT_DIMENSIONS[(format as FormatId)] ?? FORMAT_DIMENSIONS["9:16"];

    // Wichtig: `enabled`, `muted` und `volume` werden BEWUSST aus der
    // Build-Eingabe entfernt — alle drei sind reine Runtime-Properties
    // des Players. Würden wir sie mit in die Composition mischen, würde
    // jedes Toggle einen Rebuild + iframe-Reload erzwingen → sichtbares
    // Bildstottern + Audio-Aussetzer. Stattdessen bauen wir die HTML
    // immer mit `enabled: true` + `volume: 1` und steuern die echte
    // Wiedergabe via `musicMuted`/`voiceoverMuted`/`sfxMuted` und den
    // Volume-Props auf dem Player. Auch SFX folgt diesem Muster.
    //
    // `music`/`voiceover` werden hier auf eine STABILE Build-Form
    // normalisiert (nur strukturelle Felder fließen in die HTML ein).
    // Der useMemo hängt dann an einem schmalen Build-Signature-String,
    // sodass Änderungen an Lautstärke / Enabled / Mute KEINEN Rebuild
    // mehr triggern.
    const musicForBuild = useMemo(() => {
      if (!music) return undefined;
      return { ...music, enabled: true, volume: 1 };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      music?.src, music?.bed, music?.assetId, music?.label,
      music?.fromSec, music?.durSec, music?.trimStartSec,
      music?.fadeInSec, music?.fadeOutSec, music?.loop,
    ]);
    const voiceoverForBuild = useMemo(() => {
      if (!voiceover) return undefined;
      // Bei segments: jedes Segment nimmt die Track-Volumen `1` aus
      // `voiceover.volume` (das muxen wir in der Engine via baseVolume).
      // Wir lassen die Segment-Felder unverändert — fadeIn/Out/trim sind
      // strukturell und müssen ins HTML.
      return { ...voiceover, enabled: true, volume: 1 };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      voiceover?.src, voiceover?.assetId, voiceover?.label,
      voiceover?.fromSec, voiceover?.durSec, voiceover?.trimStartSec,
      voiceover?.fadeInSec, voiceover?.fadeOutSec,
      voiceover?.segments
        ?.map((s) => `${s.id}|${s.fromSec}|${s.durSec}|${s.trimStartSec}|${s.fadeInSec ?? 0}|${s.fadeOutSec ?? 0}|${s.src ?? ""}`)
        .join("·"),
    ]);
    const sfxConfigForBuild = useMemo(() => {
      if (!sfxConfig) return undefined;
      return { volume: 1, muted: false };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sfxConfig != null]);
    const composition = useMemo(() => {
      const item = {
        id: "live-reel-preview",
        kind: "reel",
        template: "HookReveal",
        category: "Reel",
        title: "Live Preview",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        props: { de: {}, en: {}, es: {}, fr: {} },
        languages: ["de"],
        formats: [format],
        status: "pending",
        outputs: [],
        scenes,
        ...(overlays && overlays.length ? { overlays } : {}),
        ...(sfxEvents && sfxEvents.length ? { sfxEvents } : {}),
        ...(sfxConfigForBuild ? { sfxConfig: sfxConfigForBuild } : {}),
        ...(safeZone ? { safeZone } : {}),
        ...(theme ? { theme } : {}),
        ...(watermark ? { watermark } : {}),
        ...(musicForBuild ? { music: musicForBuild } : {}),
        ...(voiceoverForBuild ? { voiceover: voiceoverForBuild } : {}),
      } as unknown as QueueItem;
      return buildReelComposition(item, format as FormatId, "de");
    }, [scenes, overlays, sfxEvents, sfxConfigForBuild, safeZone, theme, watermark, musicForBuild, voiceoverForBuild, format]);

    // Duration für die Transport-Bar / Player-Boundary — basiert auf den
    // STABILEN Build-Eingaben (immer "enabled: true"), damit Toggles an
    // `music.enabled` / `voiceover.enabled` die Reel-Länge nicht ändern.
    const durationInFrames = useMemo(
      () => calcReelDurationFrames(scenes, FPS, overlays, sfxEvents, musicForBuild, voiceoverForBuild),
      [scenes, overlays, sfxEvents, musicForBuild, voiceoverForBuild],
    );

    // remountKey is intentionally part of the key so the user can hard-reset
    // a stuck Player without changing scene structure.
    const playerKey = useMemo(
      () => `${reelStructureKey(scenes, format)}|r=${remountKey}|t=${theme ?? ""}`,
      [scenes, format, remountKey, theme],
    );

    const cap = maxWidth ?? (format === "9:16" ? 280 : format === "1:1" ? 400 : 520);
    const displayHeight = Math.round((cap * dims.height) / dims.width);

    const playerRef = useRef<PlayerRef | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        get player() {
          return playerRef.current;
        },
        durationInFrames,
      }),
      [durationInFrames],
    );

    const selectedScene = useMemo(
      () => (selectedUid ? scenes.find((s) => s.uid === selectedUid) ?? null : null),
      [scenes, selectedUid],
    );
    const selectedOverlay = useMemo<OverlayLayer | null>(
      () =>
        selectedUid ? (overlays ?? []).find((o) => o.uid === selectedUid) ?? null : null,
      [overlays, selectedUid],
    );

    /**
     * Stable ref-callback: only forwards a Player instance ONCE — when it
     * actually changes identity. This is the key fix for the "black screen"
     * bug: previously the ref-callback fired on every render, attaching a
     * fresh set of frameupdate / play / pause listeners each time. After a
     * dozen edits the parent had ~50 zombie listeners flipping isPlaying
     * back and forth, which made play()/pause()/seek() feel like they did
     * nothing (the last-attached listener would immediately overwrite).
     */
    const lastReportedPlayer = useRef<PlayerRef | null>(null);
    const setPlayerRef = useCallback(
      (node: PlayerRef | null) => {
        playerRef.current = node;
        if (node !== lastReportedPlayer.current) {
          // First tear down old listeners on the parent side.
          if (lastReportedPlayer.current && onPlayerReady) onPlayerReady(null);
          lastReportedPlayer.current = node;
          if (node && onPlayerReady) onPlayerReady(node);
        }
      },
      [onPlayerReady],
    );

    // Apply initial mute when the player mounts or remountKey bumps.
    useEffect(() => {
      const p = playerRef.current;
      if (!p) return;
      try {
        if (initialMuted) p.mute();
        else p.unmute();
      } catch {
        /* setVolume not yet ready — safe to ignore */
      }
    }, [initialMuted, playerKey]);

    if (!scenes.length) {
      return (
        <div className="player-shell" style={{ width: cap, height: displayHeight }}>
          <div className="empty">Noch keine Scenes auf der Timeline.</div>
        </div>
      );
    }

    // Built-in player chrome is ALWAYS off — playback is exclusively driven
    // by the global TransportBar above the timeline. clickToPlay also off so
    // clicking inside the preview can be used for layer selection / drag
    // without accidentally toggling playback.

    // Reset handler used by the in-shell error boundary's button. Falls back
    // to a local remount when the parent didn't wire onPlayerReset.
    const handleInShellReset = () => {
      if (onPlayerReset) onPlayerReset();
      else playerRef.current?.seekTo(0);
    };

    return (
      <div
        className={`player-shell edit-live ${locked ? "is-locked" : ""}`}
        style={{ width: cap, height: displayHeight, position: "relative" }}
      >
        <PlayerErrorBoundary onReset={handleInShellReset}>
          <HyperframesPlayer
            key={playerKey}
            srcDoc={composition.html}
            compositionWidth={dims.width}
            compositionHeight={dims.height}
            fps={FPS}
            controls={false}
            loop={loop}
            autoPlay={false}
            initialMuted={initialMuted}
            sfxMuted={sfxMuted || (sfxConfig ? !!sfxConfig.muted : false)}
            musicMuted={musicMuted || (music ? !music.enabled : true)}
            voiceoverMuted={voiceoverMuted || (voiceover ? !voiceover.enabled : true)}
            sfxVolume={sfxConfig?.volume ?? 1}
            musicVolume={music?.volume ?? 1}
            voiceoverVolume={voiceover?.volume ?? 1}
            displayWidth={cap}
            displayHeight={displayHeight}
            remountKey={remountKey}
            durationInFrames={durationInFrames}
            onReady={setPlayerRef}
            style={{ background: "#06070b" }}
          />
        </PlayerErrorBoundary>
        <PlayerEditOverlay
          enabled
          locked={locked}
          snap={snap}
          displayWidth={cap}
          displayHeight={displayHeight}
          compositionWidth={dims.width}
          compositionHeight={dims.height}
          watermark={editWatermark ?? watermark}
          wmSceneUid={editWatermarkSceneUid}
          onWatermarkChange={onWatermarkChange}
          selectedScene={selectedScene}
          onScenePatch={onScenePatch}
          overlays={overlays ?? []}
          selectedOverlay={selectedOverlay}
          playheadFrame={playheadFrame}
          fps={FPS}
          onSelectUid={onSelectUid}
          onOverlayPatch={onOverlayPatch}
          onAddTextOverlay={onAddTextOverlay}
          onDropElement={onDropElement}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        />
      </div>
    );
  },
);

ReelPlayerPreview.displayName = "ReelPlayerPreview";
